import * as Form from "@radix-ui/react-form";
import { fresponsive, fmobile } from "library/fullyResponsive";
import styled, { css } from "styled-components";
import { ReactComponent as ErrorSVG } from "images/form-error.svg";
import { ReactComponent as SuccessIcon } from "images/form-success.svg";
import textStyles from "styles/text";
import colors from "styles/colors";
import { ReactComponent as ChevronDownIconSVG } from "images/global/chevron.svg";

export function SelectControl({
  name,
  required = false,
}: {
  name: string;
  required?: boolean;
}) {
  return (
    <Field name={name}>
      <Form.Label />
      <SelectWrapper>
        <Form.Control asChild required={required}>
          <Select name={name} className="number-of-employees" defaultValue="">
            <option disabled value="">
              Number of Employees
            </option>
            <option value="1-50">1-50</option>
            <option value="51-250">51-250</option>
            <option value="251-500">251-500</option>
            <option value="501-1000">501-1000</option>
            <option value="1001-5000">1001-5000</option>
            <option value="5000+">5000+</option>
          </Select>
        </Form.Control>
        <ChevronDownIcon />
      </SelectWrapper>
      <Message match="valueMissing">Please select number of employees</Message>
      <ErrorIcon />
      <Success />
    </Field>
  );
}

const Field = styled(Form.Field)`
  position: relative;
`;

const iconStyles = css`
  ${fresponsive(css`
    display: none;
    position: absolute;
    right: 16px;
    top: 16px;
    height: 16px;
  `)}
`;

const areaStyles = css`
  ${textStyles.titleS};
  ${fresponsive(css`
    width: 100%;
    display: flex;
    padding: 16px;
    justify-content: space-between;
    align-items: center;
    border-radius: 10px;
    background: ${colors.greenGray02};
    border: 1px solid transparent;
    transition: 0.1s ease-in-out;
    transition-property: border, background, color;
    padding-right: 40px;
    color: ${colors.greenPastel02};

    &::placeholder {
      transition: 0.1s ease-in-out;
      transition-property: color;
      color: ${colors.greenGray01};
    }

    &:hover {
      border: 1px solid ${colors.greenSaturated02};

      &::placeholder {
        color: ${colors.greenPastel02};
      }
    }

    &:focus {
      color: ${colors.greenDark01};
      background: ${colors.white};
      border: 1px solid transparent;
      outline: none;

      &::placeholder {
        color: ${colors.charcoal200};
      }
    }

    &[data-invalid="true"] {
      border: 1px solid #f76161;
      background: ${colors.white};
      color: ${colors.greenDark01};

      &::placeholder {
        color: ${colors.charcoal200};
      }
    }

    &:autofill,
    &[data-com-onepassword-filled] {
      color: ${colors.greenDark01};
      background: ${colors.greenPastel02};
    }
  `)}
`;

const ChevronDownIcon = styled(ChevronDownIconSVG)`
  ${fresponsive(css`
    width: 16px;
    height: auto;
    position: absolute;
    right: 20px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;

    path {
      fill: ${colors.greenDark01};
    }
  `)}

  ${fmobile(css`
    width: 12px;
    height: 12px;
  `)}

  ${Field}[data-invalid="true"] & {
    display: none;
  }
`;

const Select = styled.select`
  ${fresponsive(css`
    position: relative;
    ${textStyles.titleS};
    color: ${colors.greenGray01};
    background: ${colors.greenGray02};
    width: 100%;
    height: 48px;
    padding: 0 16px;
    justify-content: space-between;
    align-items: center;
    border-radius: 10px;
    background: ${colors.greenGray02};
    transition: 0.2s ease-in-out;
    outline: none;

    &:hover {
      color: ${colors.white};
      border: 1px solid ${colors.greenSaturated02};
    }

    &:focus {
      outline-width: 1px;
      outline-style: solid;
      outline-color: #b3ed97;
    }

    &:invalid {
      color: #4b5845;
    }

    &:valid {
      color: ${colors.white};
    }

    -webkit-appearance: none !important;
    -moz-appearance: none !important;
    appearance: none !important;
    background-image: none !important;
  `)}
`;

const SelectWrapper = styled.div`
  position: relative;
  display: inline-block;
  width: 100%;
`;

const Message = styled(Form.Message)`
  ${textStyles.bodyXS}
  ${fresponsive(css`
    color: #f76161;
    position: absolute;
    top: calc(100% + 4px);
    left: 16px;
  `)}

  ${fmobile(css`
    top: calc(100% - 1px);
  `)}
`;

const ErrorIcon = styled(ErrorSVG)`
  ${iconStyles}

  ${Field}[data-invalid="true"] & {
    display: block;
  }
`;

const Success = styled(SuccessIcon)`
  ${iconStyles}

  ${Field}[data-valid="true"] & {
    display: block;
  }
`;
